<section class="flex h-full flex-col gap-6">
    <div class="flex items-center justify-between">
        <div class="text-xl text-[#141437] dark:text-white font-medium">
            Routes Management
        </div>
        <div class="flex items-center justify-end gap-2">
            <app-button
                variant="primary"
                (buttonClick)="refresh()"
                [iconName]="'refreshCcw'"
                size="sm"
                className="flex items-center justify-center gap-2 !shadow-none">
                <span class="font-medium">Refresh</span>
            </app-button>
            <div class="flex items-center gap-2 rounded-md border border-gray-200 dark:border-surface-3 bg-white dark:bg-surface-1 px-3.5 py-1.5">
                <span class="text-xs font-medium text-gray-500 dark:text-ink-subtle">
                    Total Routes
                </span>
                <span class="text-sm font-semibold text-[#141437] dark:text-white">
                    {{ totalRoutes() }}
                </span>
            </div>
        </div>
    </div>

    @if (showAudit()) {
    <!-- Audit logs screen -->
    <div class="flex min-h-0 flex-1 flex-col gap-3">
        <div class="flex shrink-0 items-center gap-2">
            <button type="button" (click)="showAudit.set(false)"
                class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-purple-600 dark:text-ink-subtle dark:hover:bg-white/5 dark:hover:text-white">
                <app-icon-v2 [size]="15" name="arrowLeft"></app-icon-v2> Back
            </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto">
            <app-audit-log-panel [entityType]="auditEntityTypes" [showProductFilter]="false"
                [expanded]="true" title="Routes Audit Trails"></app-audit-log-panel>
        </div>
    </div>
    } @else {
    <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
        <div class="flex flex-wrap items-center gap-2.5"
            [class.pointer-events-none]="isFormOpen()" [class.opacity-50]="isFormOpen()">
            @for (product of products; track product.id) {
                <button type="button"
                    [disabled]="isFormOpen()"
                    (click)="selectProduct(product.id)"
                    class="px-4 py-2 rounded-lg text-sm font-medium border transition-all active:scale-[0.98] disabled:cursor-not-allowed"
                    [class]="selectedProductId() === product.id
                        ? 'bg-purple-600 border-purple-600 text-white shadow-sm dark:bg-purple-500 dark:border-purple-500'
                        : 'bg-white dark:bg-surface-1 border-gray-200 dark:border-surface-3 text-[#141437] dark:text-ink-soft hover:border-purple-300 dark:hover:border-line-strong hover:bg-purple-50/50 dark:hover:bg-white/10'">
                    {{ product.name }}
                </button>
            }
        </div>

        <div class="flex flex-wrap items-center gap-2.5 shrink-0">
            <div class="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-surface-3 dark:bg-surface-1"
                [class.opacity-50]="isFormOpen()" [class.pointer-events-none]="isFormOpen()">
                <span class="text-xs font-medium text-[#141437] dark:text-ink-soft">Show deleted routes</span>
                <button type="button" role="switch" [attr.aria-checked]="showDeleted()" [disabled]="isFormOpen()"
                    (click)="toggleDeleted()"
                    class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
                    [class]="showDeleted() ? 'bg-purple-600' : 'bg-slate-300 dark:bg-white/15'">
                    <span class="absolute left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform"
                        [class]="showDeleted() ? 'translate-x-4' : 'translate-x-0'"></span>
                </button>
            </div>

            <button type="button" (click)="showAudit.set(true)" [disabled]="isFormOpen()"
                class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-medium text-[#141437] transition-colors hover:border-purple-300 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-surface-3 dark:bg-surface-1 dark:text-ink-soft dark:hover:border-line-strong dark:hover:text-white">
                <app-icon-v2 [size]="14" name="history"></app-icon-v2> Show audit logs
            </button>
        </div>
    </div>

    <!-- Routes hierarchy + sidebar preview -->
    <div class="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        <!-- Routes tree (takes remaining width) -->
        <div class="flex min-h-0 min-w-0 flex-1 flex-col">
            @if (isFormOpen()) {
                <!-- Create route form -->
                <div class="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-surface-3 bg-white dark:bg-surface-1">
                    <!-- Header -->
                    <div class="flex shrink-0 items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-white/5">
                        <button type="button" (click)="cancelCreate()"
                            class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-purple-600 dark:text-ink-subtle dark:hover:bg-white/5 dark:hover:text-white">
                            <app-icon-v2 [size]="15" name="arrowLeft"></app-icon-v2> Back
                        </button>
                        <div class="h-4 w-px bg-slate-200 dark:bg-white/10"></div>
                        <span class="text-sm font-semibold text-[#141437] dark:text-white">
                            {{ isEditMode() ? 'Edit Route' : (createParent() ? 'Create New Route under "' + createParent().name + '"' : 'Create New Route') }}
                        </span>
                    </div>

                    <!-- Body -->
                    <div class="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 sm:p-5">
                        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <!-- Name -->
                            <div class="space-y-1.5">
                                <label class="text-xs font-medium text-slate-600 dark:text-ink-subtle">
                                    Route Name <span class="text-rose-500">*</span>
                                </label>
                                <app-input size="sm" variant="themeBase" placeholder="e.g. Dashboard"
                                    [fullWidth]="true"
                                    [error]="nameError() ? 'A route with this name already exists' : ''"
                                    [ngModel]="draftName()" [ngModelOptions]="{ standalone: true }"
                                    (ngModelChange)="draftName.set($event)"></app-input>
                            </div>

                            <!-- Parent -->
                            <div class="space-y-1.5">
                                <label class="text-xs font-medium text-slate-600 dark:text-ink-subtle">Parent Route</label>
                                <app-dropdown variant="themeBase" size="sm" [fullWidth]="true" [clearable]="true"
                                    placeholder="None (top-level)" [options]="parentOptions()"
                                    [selectedValue]="draftParentId()"
                                    (selectionChange)="draftParentId.set($event)"></app-dropdown>
                            </div>

                            <!-- Path -->
                            <div class="space-y-1.5">
                                <label class="text-xs font-medium text-slate-600 dark:text-ink-subtle">
                                    URL Path <span class="text-rose-500">*</span>
                                </label>
                                <app-input size="sm" variant="themeBase" placeholder="route-path"
                                    [fullWidth]="true"
                                    [ngModel]="draftPath()" [ngModelOptions]="{ standalone: true }"
                                    (ngModelChange)="draftPath.set($event)"></app-input>
                            </div>

                            <!-- Icon (beside URL path) -->
                            <div class="space-y-1.5">
                                <label class="text-xs font-medium text-slate-600 dark:text-ink-subtle">Icon Name</label>
                                <div class="flex items-center gap-2">
                                    <div class="min-w-0 flex-1">
                                        <app-input size="sm" variant="themeBase" placeholder="e.g. layoutDashboard"
                                            [fullWidth]="true"
                                            [ngModel]="draftIcon()" [ngModelOptions]="{ standalone: true }"
                                            (ngModelChange)="draftIcon.set($event)"></app-input>
                                    </div>
                                    <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 dark:border-surface-3 dark:bg-white/5 dark:text-ink-soft">
                                        <app-icon-v2 [size]="18" [name]="draftIcon().trim() || 'layoutDashboard'"></app-icon-v2>
                                    </div>
                                </div>
                            </div>

                            <!-- Component Name (optional) -->
                            <div class="space-y-1.5">
                                <label class="text-xs font-medium text-slate-600 dark:text-ink-subtle">
                                    Component Name <span class="text-slate-400">(optional)</span>
                                </label>
                                <app-input size="sm" variant="themeBase" placeholder="e.g. DashboardComponent"
                                    [fullWidth]="true"
                                    [error]="componentNameError() ? 'This component name is already used' : ''"
                                    [ngModel]="draftComponentName()" [ngModelOptions]="{ standalone: true }"
                                    (ngModelChange)="draftComponentName.set($event)"></app-input>
                            </div>

                            <!-- Component Path (prefixed by visibility) -->
                            <div class="space-y-1.5">
                                <label class="text-xs font-medium text-slate-600 dark:text-ink-subtle">
                                    Component Path
                                </label>
                                <div class="flex items-stretch overflow-hidden rounded-lg border border-[#EAEAEA] bg-white transition-all focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-500/20 dark:border-line dark:bg-surface-1">
                                    <span class="flex shrink-0 items-center border-r border-[#EAEAEA] bg-slate-100 px-3 font-mono text-xs text-slate-500 dark:border-line dark:bg-white/[0.06] dark:text-ink-subtle">
                                        {{ componentPathPrefix() }}
                                    </span>
                                    <input type="text" [value]="draftComponentPath()"
                                        (input)="draftComponentPath.set($any($event.target).value)"
                                        placeholder="dashboard/dashboard.component"
                                        class="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-[#141437] placeholder:text-slate-400 focus:outline-none dark:text-white dark:placeholder:text-ink-subtle" />
                                </div>
                            </div>
                        </div>

                        <!-- Component registry notice -->
                        <div class="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 dark:border-amber-500/20 dark:bg-amber-500/10">
                            <app-icon-v2 [size]="16" name="triangleAlert" class="mt-0.5 shrink-0 text-amber-500 dark:text-amber-400"></app-icon-v2>
                            <p class="text-[12px] leading-relaxed text-amber-700 dark:text-amber-300">
                                Make sure the <span class="font-semibold">component name</span> is registered in the
                                <span class="font-semibold">component registry</span> — a route won't load in the app
                                until its component is mapped there.
                            </p>
                        </div>

                        <!-- Toggles -->
                        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <!-- Visibility -->
                            <div class="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-3 dark:border-surface-3">
                                <div class="min-w-0">
                                    <p class="text-sm font-medium text-[#141437] dark:text-white">Public</p>
                                    <p class="text-[11px] text-slate-400 dark:text-ink-subtle">{{ draftIsPublic() ? 'Public route' : 'Private route' }}</p>
                                </div>
                                <button type="button" role="switch" [attr.aria-checked]="draftIsPublic()"
                                    (click)="draftIsPublic.set(!draftIsPublic())"
                                    class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
                                    [class]="draftIsPublic() ? 'bg-purple-600' : 'bg-slate-300 dark:bg-white/15'">
                                    <span class="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                                        [class]="draftIsPublic() ? 'translate-x-5' : 'translate-x-0'"></span>
                                </button>
                            </div>

                            <!-- Show in sidebar -->
                            <div class="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-3 dark:border-surface-3">
                                <div class="min-w-0">
                                    <p class="text-sm font-medium text-[#141437] dark:text-white">Show in Sidebar</p>
                                    <p class="text-[11px] text-slate-400 dark:text-ink-subtle">{{ draftShowInSidebar() ? 'Visible in the menu' : 'Path only (hidden)' }}</p>
                                </div>
                                <button type="button" role="switch" [attr.aria-checked]="draftShowInSidebar()"
                                    (click)="draftShowInSidebar.set(!draftShowInSidebar())"
                                    class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
                                    [class]="draftShowInSidebar() ? 'bg-purple-600' : 'bg-slate-300 dark:bg-white/15'">
                                    <span class="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                                        [class]="draftShowInSidebar() ? 'translate-x-5' : 'translate-x-0'"></span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div class="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-white/5">
                        <button type="button" (click)="cancelCreate()"
                            class="inline-flex h-10 items-center rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-surface-3 dark:text-ink-soft dark:hover:bg-white/5">
                            Cancel
                        </button>
                        <app-button variant="primary" [iconName]="isEditMode() ? 'check' : 'circlePlus'"
                            [disabled]="!isCreateValid() || submitting()"
                            (buttonClick)="submitForm()" className="flex h-10 items-center justify-center gap-2 !shadow-none">
                            <span class="font-medium">{{ isEditMode() ? 'Update Route' : 'Create Route' }}</span>
                        </app-button>
                    </div>
                </div>
            } @else if (loading()) {
                <!-- Global loader (showLoader) covers the screen while loading. -->
                <div class="h-full"></div>
            } @else if (routeTree().length === 0) {
                <div class="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-200 dark:border-surface-3 text-center">
                    <app-icon-v2 [size]="28" name="layoutDashboard" class="text-slate-300 dark:text-white/20"></app-icon-v2>
                    <p class="text-sm font-medium text-slate-500 dark:text-ink-subtle">No routes found</p>
                    @if (!showDeleted()) {
                        <app-button variant="primary" size="sm" [iconName]="'circlePlus'"
                            (buttonClick)="addRoute()" className="flex items-center justify-center gap-2 !shadow-none">
                            <span class="font-medium">Create Route</span>
                        </app-button>
                    }
                </div>
            } @else {
                <div class="flex h-full flex-col rounded-2xl border border-slate-200 dark:border-surface-3 bg-white dark:bg-surface-1 p-2 sm:p-3">
                    <div class="flex shrink-0 items-center justify-end gap-1 px-1 pb-2">
                        @if (hasNesting()) {
                            <button type="button" (click)="expandAll()"
                                class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-purple-600 dark:text-ink-subtle dark:hover:bg-white/5 dark:hover:text-white">
                                <app-icon-v2 [size]="14" name="plus"></app-icon-v2> Expand all
                            </button>
                            <button type="button" (click)="collapseAll()"
                                class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-purple-600 dark:text-ink-subtle dark:hover:bg-white/5 dark:hover:text-white">
                                <app-icon-v2 [size]="14" name="minus"></app-icon-v2> Collapse all
                            </button>
                        }
                        @if (!showDeleted()) {
                            <app-button variant="primary" size="sm" [iconName]="'circlePlus'"
                                (buttonClick)="addRoute()" className="flex items-center justify-center gap-2 !shadow-none">
                                <span class="font-medium">Create Route</span>
                            </app-button>
                        }
                    </div>
                    <div class="ml-[2px] min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden border-l border-slate-200 pl-[18px] pr-1 dark:border-white/10">
                        @for (route of routeTree(); track route.id; let last = $last) {
                            <ng-container *ngTemplateOutlet="routeNode; context: { $implicit: route, depth: 0, isLast: last }"></ng-container>
                        }
                    </div>
                </div>
            }
        </div>

        <!-- Sidebar preview (hidden while viewing deleted routes) -->
        @if (!showDeleted()) {
        <div class="flex min-h-0 flex-col lg:w-72 lg:shrink-0">
            <div class="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-surface-3 bg-white dark:bg-surface-1">
                <div class="shrink-0 border-b border-slate-100 px-4 py-3 dark:border-white/5">
                    <p class="text-sm font-semibold text-[#141437] dark:text-white">Sidebar Preview</p>
                    <p class="text-[11px] text-slate-400 dark:text-ink-subtle">How the menu renders for this product</p>
                    @if (sidebarPreview().length > 0) {
                        <p class="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-purple-500 dark:text-purple-400">
                            <app-icon-v2 [size]="12" name="gripVertical"></app-icon-v2>
                            {{ isEditMode() ? 'Drag the route to reposition it' : (isFormOpen() ? 'Drag the new route to position it' : 'Drag & drop to change the order') }}
                        </p>
                    }
                </div>
                <div class="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
                    @if (sidebarPreview().length === 0) {
                        <div class="flex h-full items-center justify-center px-4 text-center text-xs text-slate-400 dark:text-ink-subtle">
                            No menu items are visible in the sidebar for this product.
                        </div>
                    } @else {
                        @for (item of sidebarPreview(); track item.id) {
                            <div [attr.draggable]="isFormOpen() ? item.isDraft : !item.isDraft"
                                (dragstart)="onPreviewDragStart(item.id, null)"
                                (dragenter)="onPreviewDragEnter(item.id, null)"
                                (dragover)="$event.preventDefault()"
                                (drop)="onPreviewDrop(item.id, null)"
                                (dragend)="onPreviewDragEnd()"
                                [class.rounded-lg]="dragOverId() === item.id"
                                [class.ring-2]="dragOverId() === item.id"
                                [class.ring-purple-400]="dragOverId() === item.id">
                                <div class="group flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors"
                                    [class]="item.isDraft
                                        ? 'bg-purple-50 ring-1 ring-purple-300 dark:bg-purple-500/10 dark:ring-purple-500/40'
                                        : 'hover:bg-purple-50 dark:hover:bg-white/5'">
                                    @if (isFormOpen() ? item.isDraft : !item.isDraft) {
                                        <app-icon-v2 [size]="14" name="gripVertical" class="shrink-0 cursor-grab text-slate-300 group-hover:text-slate-400 dark:text-white/20"></app-icon-v2>
                                    }
                                    <button type="button"
                                        (click)="item.children.length ? togglePreview(item.id) : null"
                                        class="flex min-w-0 flex-1 items-center gap-2.5"
                                        [class.cursor-default]="!item.children.length">
                                        <app-icon-v2 [size]="18" [name]="item.icon" class="shrink-0 text-slate-500 group-hover:text-purple-600 dark:text-ink-soft dark:group-hover:text-white"></app-icon-v2>
                                        <span class="flex-1 truncate text-left text-sm font-medium text-[#141437] dark:text-ink-soft">{{ item.label }}</span>
                                        @if (item.isDraft) {
                                            <span class="shrink-0 animate-pulse rounded-full bg-purple-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">New</span>
                                        } @else if (isUnconfigured(item.route)) {
                                            <span class="h-2 w-2 shrink-0 animate-pulse rounded-full bg-rose-500" title="Route not configured in component registry"></span>
                                        }
                                        @if (item.children.length) {
                                            <app-icon-v2 [size]="15" [name]="(isPreviewExpanded(item.id) || (isFormOpen() && draftParentId() === item.id)) ? 'chevronDown' : 'chevronRight'" class="shrink-0 text-slate-400 dark:text-slate-500"></app-icon-v2>
                                        }
                                    </button>
                                </div>
                                @if (item.children.length && (isPreviewExpanded(item.id) || (isFormOpen() && draftParentId() === item.id))) {
                                    <div class="ml-[26px] mt-0.5 space-y-0.5 border-l border-slate-200 pl-3 dark:border-white/10">
                                        @for (child of item.children; track child.id) {
                                            <div class="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors"
                                                [attr.draggable]="isFormOpen() ? child.isDraft : !child.isDraft"
                                                (dragstart)="onPreviewDragStart(child.id, item.id); $event.stopPropagation()"
                                                (dragenter)="onPreviewDragEnter(child.id, item.id)"
                                                (dragover)="$event.preventDefault()"
                                                (drop)="onPreviewDrop(child.id, item.id); $event.stopPropagation()"
                                                (dragend)="onPreviewDragEnd()"
                                                [class]="child.isDraft
                                                    ? 'bg-purple-50 font-medium text-purple-700 ring-1 ring-purple-300 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-500/40'
                                                    : (dragOverId() === child.id
                                                        ? 'text-slate-600 ring-2 ring-purple-400 dark:text-ink-subtle'
                                                        : 'text-slate-600 hover:bg-purple-50 dark:text-ink-subtle dark:hover:bg-white/5')">
                                                @if (isFormOpen() ? child.isDraft : !child.isDraft) {
                                                    <app-icon-v2 [size]="13" name="gripVertical" class="shrink-0 cursor-grab text-slate-300 dark:text-white/20"></app-icon-v2>
                                                }
                                                <span class="flex-1 truncate text-left">{{ child.label }}</span>
                                                @if (child.isDraft) {
                                                    <span class="shrink-0 animate-pulse rounded-full bg-purple-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">New</span>
                                                } @else if (isUnconfigured(child.route)) {
                                                    <span class="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-rose-500" title="Route not configured in component registry"></span>
                                                }
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        }
                    }
                </div>
            </div>
        </div>
        }
    </div>
    }
</section>

<!-- Recursive thread node -->
<ng-template #routeNode let-route let-depth="depth" let-isLast="isLast">
    <div class="relative">
        <!-- Thread connector -->
        <span class="absolute -left-[18px] top-[18px] h-px w-[18px] bg-slate-200 dark:bg-white/10"></span>
        @if (isLast) {
            <span class="absolute -left-[18px] top-[18px] bottom-0 w-px bg-white dark:bg-surface-1"></span>
        }

        <div class="group flex items-start gap-2.5 px-2.5 py-2 transition-colors"
            [class]="(depth === 0
                ? 'rounded-xl border border-slate-200 dark:border-surface-3 bg-white dark:bg-surface-2 hover:border-purple-300 dark:hover:border-line-strong'
                : 'rounded-lg hover:bg-slate-50 dark:hover:bg-white/[0.03]')
                + (route.subMenus?.length ? ' cursor-pointer' : '')"
            (click)="route.subMenus?.length && toggleNode(route.id)">
            <!-- Expand toggle -->
            <span class="mt-px flex h-5 w-5 shrink-0 items-center justify-center text-slate-400 transition-colors"
                [class]="route.subMenus?.length ? 'group-hover:text-purple-600 dark:group-hover:text-white' : ''">
                @if (route.subMenus?.length) {
                    <app-icon-v2 [size]="16" [name]="isExpanded(route.id) ? 'chevronDown' : 'chevronRight'"></app-icon-v2>
                } @else {
                    <span class="h-1 w-1 rounded-full bg-slate-300 dark:bg-white/20"></span>
                }
            </span>

            <!-- Route icon (configured on the route) -->
            <app-icon-v2 [size]="17" [name]="iconFor(route)"
                class="mt-px shrink-0 text-slate-400 dark:text-slate-500"></app-icon-v2>

            <!-- Details -->
            <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span class="truncate text-sm font-semibold text-[#141437] dark:text-white">{{ route.name }}</span>
                    <span class="truncate font-mono text-xs text-purple-600 dark:text-purple-300/90">/{{ route.path }}</span>

                    <span class="inline-flex items-center gap-1 text-[11px] font-medium"
                        [class]="route.status === 'ACTIVE' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'">
                        <span class="h-1.5 w-1.5 rounded-full" [class]="route.status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-rose-500'"></span>
                        {{ route.status === 'ACTIVE' ? 'Active' : 'Inactive' }}
                    </span>

                    @if (isUnconfigured(route)) {
                        <span class="inline-flex animate-pulse items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600 ring-1 ring-inset ring-rose-300 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/30"
                            title="No matching component in the component registry — this route will not load">
                            <span class="h-1.5 w-1.5 rounded-full bg-rose-500"></span>
                            Not in registry
                        </span>
                    }

                    @if (route.onlyPath && (route.onlyPath === true || route.onlyPath === 'true')) {
                        <span class="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-600 ring-1 ring-inset ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20">
                            Only Path
                        </span>
                    }

                    @if (route.subMenus?.length) {
                        <span class="text-[11px] font-medium text-slate-400 dark:text-slate-500">· {{ route.subMenus.length }} nested</span>
                    }
                </div>

                <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-ink-subtle">
                    <span class="inline-flex items-center gap-1">
                        <app-icon-v2 [size]="11" [name]="route.isPublic ? 'globe' : 'lock'" class="text-slate-400 dark:text-slate-500"></app-icon-v2>
                        {{ route.isPublic ? 'Public' : 'Private' }}
                    </span>
                    @if (route.componentName) {
                        <span class="font-mono text-slate-500 dark:text-slate-400">{{ route.componentName }}</span>
                    }
                    @if (route.module?.name) {
                        <span><span class="text-slate-400 dark:text-slate-500">Module: </span> <span class="font-mono">{{ route.module.name }}</span></span>
                    }
                    <span><span class="text-slate-400 dark:text-slate-500">Order: </span> {{ route.sortOrder }}</span>
                    <span>{{ formatDate(route.createdAt) }}</span>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                @if (showDeleted()) {
                    <button type="button" title="Recover route" (click)="$event.stopPropagation(); recoverRoute(route)"
                        class="flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-emerald-600 transition-colors hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-500/15">
                        <app-icon-v2 [size]="15" name="rotateCcw"></app-icon-v2> Recover
                    </button>
                } @else {
                    <button type="button" title="Add sub-route" (click)="$event.stopPropagation(); addRoute(route)"
                        class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-purple-50 hover:text-purple-600 dark:hover:bg-white/5 dark:hover:text-white">
                        <app-icon-v2 [size]="15" name="plus"></app-icon-v2>
                    </button>
                    <button type="button" title="Edit" (click)="$event.stopPropagation(); editRoute(route)"
                        class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-500/15 dark:hover:text-blue-400">
                        <app-icon-v2 [size]="15" name="squarePen"></app-icon-v2>
                    </button>
                    <button type="button" title="Delete" (click)="$event.stopPropagation(); deleteRoute(route)"
                        class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/15 dark:hover:text-rose-400">
                        <app-icon-v2 [size]="15" name="trash"></app-icon-v2>
                    </button>
                }
            </div>
        </div>

        <!-- Nested children -->
        @if (route.subMenus?.length && isExpanded(route.id)) {
            <div class="ml-[18px] space-y-0.5 border-l border-slate-200 pl-[18px] dark:border-white/10">
                @for (child of route.subMenus; track child.id; let lastChild = $last) {
                    <ng-container *ngTemplateOutlet="routeNode; context: { $implicit: child, depth: depth + 1, isLast: lastChild }"></ng-container>
                }
            </div>
        }
    </div>
</ng-template>
